// src/admin/pages/tradein/TradeInDrawer.tsx
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  DirectionsCar as CarIcon,
  ZoomIn as ZoomInIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AddCircleOutline as AddCircleOutlineIcon,
} from '@mui/icons-material';
import { TradeInSubmission, TRADE_IN_STATUSES, TRADE_IN_REJECTION_REASONS } from '../../../enums/tradeIn';
import ImageLightbox from '../../components/ImageLightbox';
import DrawerFooter from '../../components/DrawerFooter';

export interface TradeInDrawerSaveData {
  status: string;
  offeredPrice: string;
  internalNotes: string;
  rejectionReason: string;
}

interface TradeInDrawerProps {
  isOpen: boolean;
  submission: TradeInSubmission | null;
  onClose: () => void;
  onSave: (updates: TradeInDrawerSaveData) => Promise<void>;
  onCreateListing?: (submission: TradeInSubmission) => void;
}

const EMPTY_FORM: TradeInDrawerSaveData = { status: '', offeredPrice: '', internalNotes: '', rejectionReason: '' };

const STATUS_CHIP_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  'New Submission': 'info',
  'Under Review': 'warning',
  'Offer Made': 'warning',
  Accepted: 'success',
  Rejected: 'error',
};

const TradeInDrawer: React.FC<TradeInDrawerProps> = ({ isOpen, submission, onClose, onSave, onCreateListing }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (isOpen && submission) {
      setFormData({
        status: submission.status,
        offeredPrice: String(submission.offeredPrice || 0),
        internalNotes: submission.internalNotes || '',
        rejectionReason: submission.rejectionReason || '',
      });
      setValidationError('');
    }
  }, [isOpen, submission]);

  const handleChange = (field: keyof typeof EMPTY_FORM) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setValidationError('');
  };

  // Quick-decision shortcuts — set the status field to the terminal outcome;
  // the admin still confirms via "Save Changes" so nothing commits on a
  // single misclick, but the intent (Approve vs. Reject) is explicit and
  // separate from the free-form status dropdown used for earlier stages.
  const handleApproveClick = () => {
    setFormData((prev) => ({ ...prev, status: 'Accepted', rejectionReason: '' }));
    setValidationError('');
  };

  const handleRejectClick = () => {
    setFormData((prev) => ({ ...prev, status: 'Rejected' }));
    setValidationError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.status === 'Accepted' && (!parseFloat(formData.offeredPrice) || parseFloat(formData.offeredPrice) <= 0)) {
      setValidationError('An offered price is required before a trade-in can be accepted.');
      return;
    }
    if (formData.status === 'Rejected' && !formData.rejectionReason) {
      setValidationError('A rejection reason is required before a trade-in can be rejected.');
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isDecided = submission?.status === 'Accepted' || submission?.status === 'Rejected';

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '100%', md: 600 }, maxWidth: 600 } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="h6">Review Trade-In Submission</Typography>
              {submission?.status && (
                <Chip
                  label={submission.status}
                  size="small"
                  color={STATUS_CHIP_COLOR[submission.status] || 'default'}
                  variant="outlined"
                />
              )}
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content + footer */}
          <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CarIcon sx={{ mr: 1 }} />
                    Vehicle &amp; Owner
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {submission?.carMake} {submission?.carModel} · {submission?.carYear}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {submission?.ownerName} · {submission?.ownerPhone}
                    {submission?.ownerEmail ? ` · ${submission.ownerEmail}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {submission?.mileageKm ? `${submission.mileageKm.toLocaleString()} km` : 'Mileage not provided'}
                    {submission?.condition ? ` · ${submission.condition}` : ''}
                  </Typography>
                  {submission?.registrationNumber && (
                    <Typography variant="body2" color="text.secondary">
                      Registration No.: {submission.registrationNumber}
                    </Typography>
                  )}
                  {submission?.askingPrice ? (
                    <Typography variant="body2" color="text.secondary">
                      Asking price: {submission.askingPrice.toLocaleString()}
                    </Typography>
                  ) : null}
                  {submission?.description && (
                    <Typography variant="body2" sx={{ mt: 1.5, fontStyle: 'italic' }}>
                      "{submission.description}"
                    </Typography>
                  )}

                  {submission?.images && submission.images.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Photos ({submission.images.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {submission.images.map((src, index) => (
                          <Box
                            key={src}
                            onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
                            sx={{
                              position: 'relative', width: 72, height: 72, borderRadius: 1, overflow: 'hidden',
                              border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                              '&:hover .img-overlay': { opacity: 1 },
                            }}
                          >
                            <Box component="img" src={src} alt={`Trade-in photo ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <Box
                              className="img-overlay"
                              sx={{
                                position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.4)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: 0, transition: 'opacity 0.2s ease',
                              }}
                            >
                              <ZoomInIcon sx={{ color: '#fff', fontSize: 20 }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Review Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        label="Status"
                        value={formData.status}
                        onChange={handleChange('status')}
                      >
                        {TRADE_IN_STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Offered Price"
                        value={formData.offeredPrice}
                        onChange={handleChange('offeredPrice')}
                        helperText={formData.status === 'Accepted' ? 'Required to accept' : ' '}
                      />
                    </Grid>
                    {formData.status === 'Rejected' && (
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="Rejection Reason"
                          value={formData.rejectionReason}
                          onChange={handleChange('rejectionReason')}
                          helperText="Required to reject"
                        >
                          {TRADE_IN_REJECTION_REASONS.map((r) => (
                            <MenuItem key={r} value={r}>
                              {r}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Internal Notes"
                        value={formData.internalNotes}
                        onChange={handleChange('internalNotes')}
                      />
                    </Grid>
                  </Grid>

                  {validationError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {validationError}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* ── Decision shortcuts ── */}
              {!isDecided && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Decision
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Approve to accept this vehicle at the offered price, or reject it with a reason.
                      Confirm with "Save Changes" below.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        fullWidth
                        variant={formData.status === 'Accepted' ? 'contained' : 'outlined'}
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={handleApproveClick}
                      >
                        Approve
                      </Button>
                      <Button
                        fullWidth
                        variant={formData.status === 'Rejected' ? 'contained' : 'outlined'}
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={handleRejectClick}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* ── Accepted: convert to vehicle listing ── */}
              {submission?.status === 'Accepted' && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Vehicle Listing
                    </Typography>
                    {submission.convertedToCarId ? (
                      <Alert severity="success">
                        This trade-in has already been converted to a Car Inventory listing.
                      </Alert>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Create a new Car Inventory listing pre-filled from this accepted trade-in.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => submission && onCreateListing?.(submission)}
                        >
                          Create Vehicle Listing
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>

            <DrawerFooter>
              <Button onClick={onClose} variant="outlined" disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving && <CircularProgress size={16} />}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DrawerFooter>
          </Box>
        </Box>
      </Drawer>

      <ImageLightbox
        images={submission?.images || []}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      <Backdrop
        open={saving}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Updating Submission…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default TradeInDrawer;
